// Full-screen POS page at /pos.
// Layout: toolbar (tabs+barcode+chips) | left=cart | right=payment panel (lg+)
//
// Serial-first flow:
//   BarcodeInput is given a serialResolver (useScanResolve sell-context).
//   kind=serial&ok → ensureDraft → add/find line → attach serial via updateItem.
//   kind=serial&!ok → toast reason.
//   kind=variant → existing onAdd/onMultiple flow unchanged.
//
// Per-line serial scan: CartItemList threads serialResolver + onAttach/onDetach
//   down to CartItemCard. Each item's SerialArea resolves independently.
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { WarehouseIcon } from "lucide-react";
import { usePosCart, type PaymentPart } from "./use-pos-cart";
import { CartItemList } from "./cart-item-list";
import { ProductSearchModal } from "./product-search-modal";
import { PaymentPanel, type PaymentView } from "./payment-panel";
import { MobilePaymentBar } from "./mobile-payment-bar";
import { PosToolbar } from "./pos-toolbar";
import { apiGet } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { useScanResolve, type ScanContext, type ScanResult } from "@/features/inventory/use-scan-resolve";
import { useAuthStore } from "@/lib/auth/store";
import { useWarehouses, initialWarehouseId, warehouseStorageKey, type SalesChannel } from "@/features/inventory/use-warehouses";

interface CouponResult { code: string; amountOff: number; }

export function PosScreenPage() {
  const {
    order,
    drafts,
    addItem,
    updateItem,
    removeItem,
    patchHeader,
    commit,
    createDraft,
    ensureDraft,
    closeDraft,
    deleteDraft,
    resumeDraft,
    clearCart,
    orderType,
    setOrderType,
    imageCache,
    cacheVariantImage,
    warehouseId,
    setWarehouseId,
  } = usePosCart();

  // Manager/admin may truly purge a draft from the server; cashiers only hide
  // the tab client-side (existing behavior).
  const role = useAuthStore((s) => s.user?.role);
  const canDeleteDraft = role === "admin" || role === "manager";
  // Quyền sửa đơn giá / chiết khấu: admin luôn có, còn lại theo cờ can_edit_price.
  const canEditPrice = useAuthStore((s) => s.user?.role === "admin" || !!s.user?.canEditPrice);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentView, setPaymentView] = useState<PaymentView>("summary");
  const [coupon, setCoupon] = useState<CouponResult | null>(null);

  // Warehouse list (from the kho module).
  const { data: warehouses = [] } = useWarehouses();

  // Kênh bán hiện tại (Lẻ/Sỉ) quyết định kho mặc định:
  //   retail    → Kho Bán Lẻ
  //   wholesale → Kho chính (kho mặc định của hệ thống)
  const channel: SalesChannel = orderType === "wholesale" ? "wholesale" : "retail";

  // Mặc định kho theo kênh; áp lại khi gạt Lẻ/Sỉ, nhưng CHỈ khi chưa có đơn đang
  // mở (đơn đang mở đã khóa dropdown kho và giữ kho lúc tạo). Lựa chọn kho thủ
  // công được giữ qua localStorage theo kênh (đọc trong initialWarehouseId).
  useEffect(() => {
    if (order || warehouses.length === 0) return;
    const init = initialWarehouseId(warehouses, channel);
    if (init) setWarehouseId(init);
  }, [warehouses, channel, order, setWarehouseId]);

  const onWarehouseChange = useCallback(
    (id: string) => {
      setWarehouseId(id);
      try {
        localStorage.setItem(warehouseStorageKey(channel), id);
      } catch {
        /* ignore storage errors */
      }
    },
    [setWarehouseId, channel],
  );

  // Build the sell-context for useScanResolve. The hook is always instantiated
  // (hooks must not be conditional); when no draft exists orderId="" and the
  // resolver is not called (serial scan button is gated on order existing or we
  // call ensureDraft first in the handleSerialAdd callback below).
  const sellCtx: ScanContext = useMemo(
    () =>
      order?.id
        ? { context: "sell", orderId: order.id }
        : { context: "sell", orderId: "" },
    [order?.id],
  );
  const { resolve: resolveSerial } = useScanResolve(sellCtx);

  // The draft id resolved by the most recent serial-first barcode scan. The scan
  // creates the draft lazily (resolveWithDraft) milliseconds before handleSerialAdd
  // runs, so `order` state is not yet committed; this ref carries the authoritative
  // id into the add/attach chain without relying on a re-render.
  const scanDraftIdRef = useRef<string>("");

  // resolveWithDraft: ensures a draft exists before calling the scan endpoint.
  // Handles the chicken-and-egg: the sell-context scan needs an orderId so the
  // server can derive the warehouse, but a retail draft is created lazily on
  // first item add. For serial-first barcode scan we create the draft upfront
  // here, then call /v1/scan with the now-known orderId.
  const resolveWithDraft = useCallback(
    async (q: string): Promise<ScanResult> => {
      const orderId = await ensureDraft();
      scanDraftIdRef.current = orderId;
      return apiGet<ScanResult>("/v1/scan", { context: "sell", orderId, q });
    },
    [ensureDraft],
  );

  // Global keyboard shortcuts.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "F2") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "F7") { e.preventDefault(); if (order) setPaymentView("bank"); }
      // F8 (QR VietQR) tạm ẩn — SePay chưa cấu hình. Bật lại khi QR_ENABLED=true (payment-panel).
      if (e.key === "F9") { e.preventDefault(); if (order) setPaymentView("cash"); }
      if (e.key === "F10") { e.preventDefault(); if (order) setPaymentView("mixed"); }
      if (e.key === "F11") { e.preventDefault(); handleNewOrder(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const handleAddVariant = useCallback((variantId: string, imageUrl?: string) => {
    cacheVariantImage(variantId, imageUrl);
    addItem.mutate({ variantId }, {
      onError: (err) => toast.error(translateApiError(err)),
    });
  }, [addItem, cacheVariantImage]);

  // Serial-first barcode callback: called by BarcodeInput when kind=serial&ok.
  // Find/create the line then attach the serial. Every step targets the draft id
  // resolved by the scan (scanDraftIdRef) rather than `order` state, which lags
  // the just-created draft within the same tick and would otherwise make the
  // attach throw "no active order" — leaving the product added but no serial.
  const handleSerialAdd = useCallback(
    (variantId: string, serialId: string) => {
      const orderId = scanDraftIdRef.current || order?.id || undefined;
      const existingItem = (order?.items ?? []).find((it) => it.variantId === variantId);

      if (existingItem) {
        const existingIds = (existingItem.serials ?? []).map((s) => s.id);
        if (existingIds.includes(serialId)) {
          toast.info("Serial đã có trong đơn hàng");
          return;
        }
        const mergedIds = [...existingIds, serialId];
        updateItem.mutate(
          { orderId, itemId: existingItem.id, serialIds: mergedIds, quantity: mergedIds.length },
          { onError: (err) => toast.error(translateApiError(err)) },
        );
      } else {
        // No line in current state — add the variant, then attach the serial.
        // Derive the merged serial set + quantity from the server response so a
        // concurrent/rapid same-variant scan can't drop a previously-attached serial.
        addItem.mutate(
          { variantId, orderId },
          {
            onSuccess: (updatedOrder) => {
              const newItem = (updatedOrder.items ?? []).find(
                (it) => it.variantId === variantId,
              );
              if (!newItem) {
                toast.error("Không thể gắn serial: dòng vừa tạo không tìm thấy");
                return;
              }
              const existingIds = (newItem.serials ?? []).map((s) => s.id);
              if (existingIds.includes(serialId)) {
                toast.info("Serial đã có trong đơn hàng");
                return;
              }
              const mergedIds = [...existingIds, serialId];
              updateItem.mutate(
                {
                  orderId: updatedOrder.id,
                  itemId: newItem.id,
                  serialIds: mergedIds,
                  quantity: newItem.quantity,
                },
                { onError: (err) => toast.error(translateApiError(err)) },
              );
            },
            onError: (err) => toast.error(translateApiError(err)),
          },
        );
      }
    },
    [order, addItem, updateItem],
  );

  const handleMultipleResults = useCallback((results: Array<{ id: string }>) => {
    if (results.length > 0) {
      setSearchQuery("");
      setSearchOpen(true);
    }
  }, []);

  const handleUpdateItem = useCallback((itemId: string, updates: { qty?: number; discount?: string; note?: string; unitPrice?: string }) => {
    updateItem.mutate({
      itemId,
      quantity: updates.qty,
      discount: updates.discount,
      unitPrice: updates.unitPrice,
      note: updates.note,
    }, {
      onError: (err) => toast.error(translateApiError(err)),
    });
  }, [updateItem]);

  const handleRemoveItem = useCallback((itemId: string) => {
    removeItem.mutate(itemId);
  }, [removeItem]);

  // Serial attach/detach handlers threaded to CartItemList → CartItemCard → SerialArea.
  const handleAttachSerial = useCallback(
    (itemId: string, mergedSerialIds: string[]) => {
      updateItem.mutate(
        { itemId, serialIds: mergedSerialIds },
        { onError: (err) => toast.error(translateApiError(err)) },
      );
    },
    [updateItem],
  );

  const handleDetachSerial = useCallback(
    (itemId: string, remainingSerialIds: string[]) => {
      updateItem.mutate(
        { itemId, serialIds: remainingSerialIds },
        { onError: (err) => toast.error(translateApiError(err)) },
      );
    },
    [updateItem],
  );

  const handleDiscount = useCallback((amount: string) => {
    patchHeader.mutate({ discountAmount: amount });
  }, [patchHeader]);

  // Tên NV bán hàng — lưu lên header đơn, in trên hóa đơn.
  const handleSalesperson = useCallback((name: string) => {
    patchHeader.mutate({ salesperson: name });
  }, [patchHeader]);

  const handleSelectCustomer = useCallback((c: { id: string } | null) => {
    patchHeader.mutate({ customerId: c?.id ?? undefined });
  }, [patchHeader]);

  // Commit with onError to surface serial-validation failures (e.g. race where
  // another till sold the same unit between our scan and commit).
  const payWith = useCallback((
    paymentMethod: string,
    tenderedAmount: number,
    couponCode?: string,
    reason?: string,
    payments?: PaymentPart[],
  ) => {
    commit.mutate(
      { paymentMethod, tenderedAmount, payments, couponCode, reason },
      {
        onSuccess: () => {
          setPaymentView("summary");
          setCoupon(null);
        },
        onError: (err: unknown) => {
          // Vietnamese serial-validation message comes through translateApiError
          // because the BE returns it as a details string (e.g. "cần đúng 1 serial, đã gắn 0").
          toast.error(translateApiError(err));
        },
      }
    );
  }, [commit]);

  const handlePayConfirm = useCallback((tenderedAmount: number, couponCode?: string, reason?: string) => {
    payWith("cash", tenderedAmount, couponCode, reason);
  }, [payWith]);

  // Chuyển khoản thủ công (khách đã CK) — ghi nhận như tiền mặt, method bank_manual.
  const handlePayBank = useCallback((tenderedAmount: number, couponCode?: string, reason?: string) => {
    payWith("bank_manual", tenderedAmount, couponCode, reason);
  }, [payWith]);

  // Hỗn hợp: 1 đơn vừa tiền mặt vừa CK. `payments` là nguồn sự thật — BE bỏ qua
  // paymentMethod/tenderedAmount khi có nó (2 tham số đầu chỉ để giữ chữ ký cũ).
  const handlePayMixed = useCallback((payments: PaymentPart[], couponCode?: string, reason?: string) => {
    const tendered = payments.reduce((sum, p) => sum + p.amount, 0);
    payWith("cash", tendered, couponCode, reason, payments);
  }, [payWith]);

  const handleQRSuccess = useCallback(() => {
    setPaymentView("summary");
    setCoupon(null);
    if (order?.id) {
      resumeDraft(order.id).catch(() => {/* non-blocking */});
    }
  }, [order, resumeDraft]);

  // Hide the tab client-side and switch to another draft (or clear). Shared by
  // both the cashier path and the manager path's success handler.
  const finishCloseTab = useCallback((orderId: string) => {
    closeDraft(orderId);
    if (order?.id === orderId) {
      setPaymentView("summary");
      setCoupon(null);
      const other = drafts.find((d) => d.id !== orderId);
      if (other) {
        resumeDraft(other.id);
      } else {
        clearCart();
      }
    }
  }, [order, drafts, closeDraft, resumeDraft, clearCart]);

  const handleCloseDraft = useCallback((orderId: string) => {
    if (canDeleteDraft) {
      // Manager/admin: purge server-side, and only hide the tab once the delete
      // succeeds — a failed delete (e.g. order is no longer a draft) must not
      // hide a still-live order from this browser forever.
      deleteDraft.mutate(orderId, {
        onSuccess: () => finishCloseTab(orderId),
        onError: (err) => toast.error(translateApiError(err)),
      });
    } else {
      // Cashier: client-side hide only (unchanged behavior).
      finishCloseTab(orderId);
    }
  }, [canDeleteDraft, deleteDraft, finishCloseTab]);

  const handleNewOrder = useCallback(() => {
    setPaymentView("summary");
    setCoupon(null);
    createDraft().catch((err: unknown) => {
      toast.error(translateApiError(err));
    });
  }, [createDraft]);

  const items = order?.items ?? [];
  const subtotal = parseFloat(order?.subtotal ?? "0");
  // partial_paid (khách trả thiếu, ghi nợ) cũng là xong đơn — nếu chỉ check
  // "paid" thì màn thành công không hiện và thu ngân bấm thanh toán lại → 400.
  const isPaid = order?.status === "paid" || order?.status === "partial_paid";

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      {/* Warehouse selector row — kho bán for new orders (defaults to Kho Bán Lẻ) */}
      <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-1.5 text-xs shrink-0">
        <WarehouseIcon size={14} className="shrink-0 text-muted-foreground" />
        <span className="font-medium text-muted-foreground">Kho bán:</span>
        <select
          value={warehouseId ?? ""}
          onChange={(e) => onWarehouseChange(e.target.value)}
          disabled={!!order}
          title={order ? "Xong đơn hiện tại để đổi kho cho đơn mới" : "Chọn kho xuất hàng cho đơn"}
          className="rounded-md border border-input bg-background px-2 py-1 text-xs font-medium focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
        >
          {warehouses.length === 0 && <option value="">Đang tải…</option>}
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>
      </div>

      {/* Unified toolbar: tabs + barcode + customer/coupon chips */}
      <PosToolbar
        drafts={drafts}
        activeOrderId={order?.id}
        onSelectDraft={resumeDraft}
        onCloseDraft={handleCloseDraft}
        onNewOrder={handleNewOrder}
        onAddVariant={handleAddVariant}
        onMultipleResults={handleMultipleResults}
        barcodeDisabled={isPaid || addItem.isPending || !warehouseId}
        selectedCustomerId={order?.customerId}
        onSelectCustomer={handleSelectCustomer}
        orderType={orderType}
        onSetOrderType={setOrderType}
        order={order}
        isPaid={isPaid}
        subtotal={subtotal}
        coupon={coupon}
        onApplyCoupon={setCoupon}
        loading={addItem.isPending}
        serialResolver={resolveWithDraft}
        onSerialAdd={handleSerialAdd}
        onSerialError={(reason) => toast.error(reason)}
      />

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: cart — pb-16 on mobile reserves space above the sticky bar */}
        <div className="flex flex-1 flex-col overflow-auto pb-16 lg:pb-0">
          {isPaid ? (
            <div className="flex flex-1 items-center justify-center flex-col gap-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {order?.status === "partial_paid" ? "Đã xác nhận đơn (có ghi nợ)!" : "Thanh toán thành công!"}
              </div>
              <div className="text-muted-foreground">Đơn {order?.code}</div>
              {parseFloat(order?.debtAmount ?? "0") > 0 && (
                <div className="text-sm font-medium text-amber-600">
                  Khách còn nợ: {new Intl.NumberFormat("vi-VN").format(parseFloat(order?.debtAmount ?? "0"))} ₫
                </div>
              )}
              <button
                onClick={handleNewOrder}
                className="rounded-md bg-primary px-6 py-2 text-primary-foreground font-medium"
              >
                Đơn hàng mới (F11)
              </button>
            </div>
          ) : (
            <CartItemList
              items={items}
              imageMap={imageCache}
              onUpdateItem={handleUpdateItem}
              onRemoveItem={handleRemoveItem}
              loading={addItem.isPending || updateItem.isPending || removeItem.isPending}
              canEditPrice={canEditPrice}
              serialResolver={resolveSerial}
              onAttachSerial={handleAttachSerial}
              onDetachSerial={handleDetachSerial}
            />
          )}
        </div>

        {/* Right: payment panel — hidden on mobile */}
        <div className="hidden lg:flex w-[360px] shrink-0 border-l bg-card flex-col">
          {order && (
            <div className="border-b px-4 py-2 text-xs text-muted-foreground font-mono">
              {order.code}
            </div>
          )}
          <PaymentPanel
            order={order}
            coupon={coupon}
            onDiscount={handleDiscount}
            onSalesperson={handleSalesperson}
            onPayCash={handlePayConfirm}
            onPayBank={handlePayBank}
            onPayMixed={handlePayMixed}
            onPayQR={() => {}}
            onQRSuccess={handleQRSuccess}
            onNewOrder={handleNewOrder}
            loading={commit.isPending}
            view={paymentView}
            onViewChange={setPaymentView}
            selectedCustomerId={order?.customerId}
            onSelectCustomer={handleSelectCustomer}
            canEditPrice={canEditPrice}
          />
        </div>
      </div>

      {/* Mobile sticky bottom payment bar — hidden on lg+ */}
      <MobilePaymentBar
        itemCount={items.length}
        order={order}
        coupon={coupon}
        onDiscount={handleDiscount}
        onSalesperson={handleSalesperson}
        onPayCash={handlePayConfirm}
        onPayBank={handlePayBank}
        onPayMixed={handlePayMixed}
        onPayQR={() => {}}
        onQRSuccess={handleQRSuccess}
        onNewOrder={handleNewOrder}
        loading={commit.isPending}
        view={paymentView}
        onViewChange={setPaymentView}
        canEditPrice={canEditPrice}
      />

      {/* Modals — all preserved */}
      <ProductSearchModal
        open={searchOpen}
        initialQuery={searchQuery}
        onSelect={handleAddVariant}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  );
}
